import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import { FC, useContext, useEffect } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import ButtonOrVideoModal from "@/components/ui/ButtonOrVideoModal/ButtonOrVideoModal";
import Image from "@/components/ui/Image/Image";
import { ModalsContext } from "@/context/ModalsContext";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { MediaBannerHighlightContent, MediaBannerHighlightType } from "@/types";
import cn from "@/utils/cn";
import { formatDateLong } from "@/utils/dateUtils";

export interface Props {
  content: MediaBannerHighlightContent;
  type: MediaBannerHighlightType | string;
  wrappedText?: boolean;
  projectInfo?: {
    pixelStreaming?: boolean;
    pixelStreamingProjectId?: string;
  };
}

const MediaBannerHighlightCardContent: FC<Props> = ({ content, type, wrappedText, projectInfo }) => {
  const locale = useLocale();
  const t = useTranslations();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { setMetaverseWalkthroughModalOpen, setAdditionalInfo, setGetInTouchModalOpen } = useContext(ModalsContext);
  useEffect(() => {
    setAdditionalInfo((prev: Record<string, any>) => ({
      ...prev,
      pixelStreamingProjectId: projectInfo?.pixelStreamingProjectId,
    }));
  }, [projectInfo?.pixelStreamingProjectId, setAdditionalInfo]);
  return (
    <div
      className={cn(
        "absolute top-0 left-0 h-full flex flex-col md:static z-[1] md:box-material-primary md:backdrop-blur-[100px] text-center pt-[32px] md:px-l md:py-m text-white rounded-xl md:my-s w-full",
        content.logo ? "justify-between" : "justify-end",
        // type !== MediaBannerHighlightType.BOXED_CAROUSEL &&
        //   (!content.description || (content.description && wrappedText)) &&
        //   "box-material-primary backdrop-blur-[100px] h-auto static",
        type == MediaBannerHighlightType.FULL_SCREEN && wrappedText && "box-material-primary backdrop-blur-[100px] h-auto static ",
      )}
    >
      {content.logo && <Image className="mx-auto mb-[16px] w-[140px]" src={content.logo.url} alt={content.logo.title || ""} width={140} />}
      <div
        className={cn(
          "px-m md:px-0 pb-l md:pb-0 media-highlight-content relative",
          // (type == MediaBannerHighlightType.FULL_SCREEN || type == MediaBannerHighlightType.BOXED) &&
          //   (!content.description || (content.description && wrappedText))
          //   ? "after:!rounded-none"
          //   : "pt-3xl md:pt-0 text-left md:text-center",
          type == MediaBannerHighlightType.FULL_SCREEN && wrappedText ? "items-center" : " pt-xl md:pt-0 text-left md:text-center",
          type == MediaBannerHighlightType.FULL_SCREEN && !wrappedText && "after:!rounded-none",
        )}
      >
        {content?.eventDate && (
          <>
            <div className="text-s text-white tracking-[3px]">{t("title.label.nextEvent")}</div>
            <div className="mt-xs mb-m">
              <span className="uppercase text-s bg-gold-20 text-black p-xs rounded-sm p-xxs">{formatDateLong(content.eventDate, locale)}</span>
            </div>
          </>
        )}
        {content.title && (
          <h3
            className={`font-primary text-l md:text-[29px] font-medium uppercase accent-font accent-title break-words hyphens-auto ${isMobile ? "text-center" : ""}`}
          >
            {content.title}
          </h3>
        )}
        {content.subtitle && <h4 className="uppercase font-normal mt-m text-xs">{content.subtitle}</h4>}
        {content.description && <p className="font-normal text-m mt-[24px]">{content.description}</p>}

        <div
          className={`flex flex-col justify-center items-baseline gap-xxs mb-[${wrappedText ? "32px" : "16px"}] pb-[24px] md:pb-0`}
          style={{ alignItems: "center" }}
        >
          {/* TODO currently not implemented properly */}
          {content.primaryCta && (
            <ButtonOrVideoModal
              cta={{ ...content.primaryCta, projectData: content.projectData }}
              btnClasses={cn(
                "w-full mt-[40px] md:mt-l inline-flex border-none justify-center md:mx-auto btn-primary-white whitespace-nowrap",
                content.primaryCta.type !== "videoModal" && "btn btn-medium btn-primary-white shadow-strong",
                content.secondaryCta && "w-full",
                !(type == MediaBannerHighlightType.FULL_SCREEN && !wrappedText) && "md:mx-auto",
              )}
              btnSize={ButtonSize.MEDIUM}
              isMetaverseCta
              title={content?.title || ""}
              componentName={content?.componentName || ""}
            />
          )}
          {projectInfo?.pixelStreaming && (
            <>
              {/* <Button
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.WHITE}
                customClass="w-full mt-m inline-flex justify-center"
                onClick={() => setMetaverseWalkthroughModalOpen(true)}
              >
                {t("button.label.metaverse")}
              </Button> */}
              <Button
                size={ButtonSize.MEDIUM}
                // variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.WHITE}
                customClass="w-full mt-m inline-flex justify-center"
                // onClick={() => setGetInTouchModalOpen(true)}
                onClick={() => setMetaverseWalkthroughModalOpen(true)}
              >
                {t("button.label.metaverse.bookAppointment")}
              </Button>
            </>
          )}
          {content.secondaryCta && (
            <Button
              href={getLink(content?.secondaryCta, locale)}
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.SECONDARY}
              theme={ButtonTheme.WHITE}
              customClass="w-full mt-m inline-flex justify-center"
              onClick={() => {
                ctaClickTracking(content?.secondaryCta?.label || "", "MediaBannerHighlightCardContent", content?.secondaryCta?.label || "");
              }}
            >
              {content.secondaryCta.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaBannerHighlightCardContent;
